// src/pages/Historico/components/ChartTabNavigation.jsx
import React, { useState } from 'react';
import {
    Box, Tabs, Tab, Stack, IconButton, CircularProgress,
    Menu, MenuItem, ListItemIcon, ListItemText, Tooltip,
    useTheme, useMediaQuery,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { GraficoTipo } from '../../../infra/api/GraficoApi';
import { DOWNLOAD_TYPES } from '../utils/chartDownload';

export const TABS = [
    { key: GraficoTipo.SPECTRAL_TURBIDIMETRY, label: 'Turbidimetria' },
    { key: GraficoTipo.SPECTRAL_NEPHELOMETRY, label: 'Nefelometria' },
    { key: GraficoTipo.SPECTRAL_FLUORESCENCE, label: 'Fluorescência' },
    // Depois dos espectrais; no plano LEITE vai para a primeira posição (perfil
    // `frequenciaPrimeiro`, ver utils/ordemAbas).
    { key: GraficoTipo.FREQUENCIA, label: 'Frequências' },
    { key: GraficoTipo.TEMPERATURA, label: 'Temperaturas' },
    { key: GraficoTipo.TEMPERATURA_SAMPLE, label: 'Temp. Amostra' },
    { key: GraficoTipo.TEMPERATURA_SAMPLE_CTRL, label: 'Amostra + Ctrl' },
    { key: GraficoTipo.TEMPERATURA_CORE_CTRL, label: 'Núcleo + Ctrl' },
    { key: GraficoTipo.FONTE_ALIMENTACAO, label: 'Fonte' },
    { key: GraficoTipo.PASTILHA_PELTIER, label: 'Peltier' },
    { key: GraficoTipo.AGITADOR_MAGNETICO, label: 'Agitador Magnético' },
    { key: GraficoTipo.CONTROL_STATE, label: 'Control State' },
];

/**
 * Graficos que descrevem a AMOSTRA — o que todo papel enxerga.
 *
 * Derivado de DOWNLOAD_TYPES (o conjunto do CSV) mais "Temp. Amostra", que nao e
 * telemetria de torre: e o recorte do canal `sample`, o dado mais direto sobre a
 * amostra. Derivar em vez de escrever uma segunda lista mantem tela e arquivo
 * amarrados — um grafico acrescentado ao CSV entra na tela sozinho.
 *
 * Mais "Frequências" (pedido do usuario, 2026-10-09): a frequencia ressonante e o sinal
 * que o modelo de CBT do leite le — descreve a amostra, nao so a torre. Fica depois dos
 * tres espectrais; no LEITE, em primeiro (e o sinal do resultado).
 *
 * Os demais sao parametros de funcionamento da torre (Peltier, fonte, malhas de
 * controle), uteis para diagnostico de hardware e so para ADMIN.
 *
 * NAO e controle de acesso: o endpoint POST /grafico segue aberto a qualquer
 * autenticado e o CSV continua completo para todos. E omissao de UI.
 */
export const TIPOS_AMOSTRA = new Set([
    ...DOWNLOAD_TYPES,
    GraficoTipo.TEMPERATURA_SAMPLE,
    GraficoTipo.FREQUENCIA,
]);

export default function ChartTabNavigation({
    tabs = TABS,
    activeTab,
    onTabChange,
    onRefresh,
    onDownload,
    onDownloadCsv,
    onDownloadXlsx,
    loadingCharts,
    isDownloading,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // A guarda da pagina reposiciona a aba ativa quando ela nao e visivel para o papel,
    // mas isso acontece num efeito — por um frame o `value` estaria fora da lista, e o
    // MUI reclamaria no console. `false` significa "nenhuma aba selecionada".
    const valorAba = tabs.some((t) => t.key === activeTab) ? activeTab : false;

    const [downloadAnchor, setDownloadAnchor] = useState(null);
    const fecharDownload = () => setDownloadAnchor(null);

    const baixar = (acao) => () => {
        fecharDownload();
        acao?.();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                borderRadius: 2,
            }}
        >
            {/* Rolagem em vez do antigo corte com menu "Mais": esconder metade das abas
                atras de um dropdown e pior que rolar, que e gesto conhecido no celular.
                Com o filtro por papel, a maioria dos usuarios ve 5 abas e nem rola. */}
            <Tabs
                value={valorAba}
                onChange={(_, novaAba) => onTabChange(novaAba)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        minHeight: 48,
                        minWidth: 'auto',
                        px: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    },
                }}
            >
                {tabs.map((tab) => (
                    <Tab key={tab.key} label={tab.label} value={tab.key} />
                ))}
            </Tabs>

            {/* Acoes: um botao de download com menu no lugar dos tres icones soltos —
                no celular eles competiam com as abas pela mesma linha. */}
            <Stack
                direction="row"
                spacing={0.25}
                alignItems="center"
                sx={{ px: { xs: 0.5, sm: 1.5 }, flexShrink: 0 }}
            >
                {loadingCharts && !isDownloading && (
                    <CircularProgress size={18} sx={{ mr: 0.5 }} />
                )}

                <Tooltip title="Baixar dados">
                    <span>
                        <IconButton
                            onClick={(e) => setDownloadAnchor(e.currentTarget)}
                            disabled={isDownloading}
                            color="primary"
                            size={isMobile ? 'small' : 'medium'}
                            aria-label="Baixar dados"
                        >
                            {isDownloading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <Lucide.Download size={isMobile ? 18 : 20} />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title="Atualizar dados">
                    <span>
                        <IconButton
                            onClick={onRefresh}
                            disabled={loadingCharts || isDownloading}
                            size={isMobile ? 'small' : 'medium'}
                            aria-label="Atualizar dados"
                        >
                            <Lucide.RefreshCw size={isMobile ? 18 : 20} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            <Menu
                anchorEl={downloadAnchor}
                open={Boolean(downloadAnchor)}
                onClose={fecharDownload}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={baixar(onDownloadCsv)}>
                    <ListItemIcon>
                        <Lucide.FileText size={18} />
                    </ListItemIcon>
                    <ListItemText primary="CSV" secondary="Planilha em texto" />
                </MenuItem>
                <MenuItem onClick={baixar(onDownloadXlsx)}>
                    <ListItemIcon>
                        <Lucide.Table size={18} />
                    </ListItemIcon>
                    <ListItemText primary="Excel" secondary="Arquivo .xlsx" />
                </MenuItem>
                <MenuItem onClick={baixar(onDownload)}>
                    <ListItemIcon>
                        <Lucide.Braces size={18} />
                    </ListItemIcon>
                    <ListItemText primary="JSON" secondary="Dataset completo" />
                </MenuItem>
            </Menu>
        </Box>
    );
}
